import React, { useEffect, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box } from '@chakra-ui/react';

import NoResultsMessage from '../../components/NoResultsMessage';
import Page from '../../components/Page';
import { buildQuickFilterOptions } from '../../components/search/util';
import LoadingSpinner from '../../components/Spinner';
import jobsImage from '../../noJobsImage.png';
import { setCurrentPage } from '../../redux/modules/app';
import { getCompanyDepartments, getCompanyUsers, selectCompanyDepartments, selectCompanyUsers } from '../../redux/modules/company';
import { getJobSelectOptions, unmountJobsListSearch } from '../../redux/modules/jobs';
import { jobSearch } from '../../redux/modules/jobSearch';
import { globalRequirementTemplateBuilder } from '../../redux/reducer';

import JobRow from './components/JobRow';
import JobsPageHeader from './components/JobsPageHeader';
import buildListJobsQuickFilters from './quickfilters';

const JobsPage = () => {
  const dispatch = useDispatch();
  const { companyId } = useParams();

  const companyDepartments = useSelector(selectCompanyDepartments);
  const companyUsers = useSelector(selectCompanyUsers);
  const jobSearchData = useSelector(jobSearch.selectData);
  const jobSearchPageInfo = useSelector(jobSearch.selectPageInfo);
  const jobSearchLoading = useSelector(jobSearch.selectIsLoading);
  const quickFilters = useSelector(jobSearch.selectQuickFilterOptions);

  const scrollRef = useRef(null);

  useEffect(() => {
    dispatch(setCurrentPage('projects'));
    }, []); // eslint-disable-line

  useEffect(() => {
    dispatch(getCompanyUsers(companyId));
    dispatch(getCompanyDepartments(companyId));
    dispatch(globalRequirementTemplateBuilder.listRequirementTemplateFolders());
    dispatch(globalRequirementTemplateBuilder.listAllPublishedTemplates());

    dispatch(jobSearch.setColumns([
      { id: 'name', accessor: 'name.keyword', sortKey: 'name.keyword', sortName: 'name' },
      { id: 'number', accessor: 'number.keyword' },
      { id: 'owner', accessor: 'owner.id' },
      { id: 'department', accessor: 'department.id' },
      { id: 'active_insureds', accessor: 'stats.active_insureds.keyword' },
      { id: 'approval_percentage', accessor: 'stats.approval_percentage.keyword' },
      { id: 'status', accessor: 'status', sortKey: 'status.keyword', sortName: 'status' }
    ]));

    dispatch(jobSearch.setBaseFilters([
      { key: 'company.id', value: companyId },
      { key: 'status.keyword', value: 'active' }
    ]));
  }, [companyId, dispatch]);

  useEffect(() => {
    if (companyDepartments.length && companyUsers?.length) {
      dispatch(jobSearch.setQuickFilterOptions(buildQuickFilterOptions(
        buildListJobsQuickFilters(),
        {
          department: companyDepartments.map(dept => ({ departmentId: dept.departmentId })),
          owner: companyUsers.map(user => ({ userId: user.userId }))
        }
      )));

      dispatch(jobSearch.getPage());
    }
  }, [companyDepartments, companyUsers, dispatch]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.el.scrollTo(0, 0);
    }
  }, [quickFilters]);

  useEffect(() => {
    if (companyUsers && companyDepartments) {
      dispatch(getJobSelectOptions());
    }
  }, [companyUsers, dispatch, companyDepartments]);

  useEffect(() => () => {
    dispatch(unmountJobsListSearch());
  }, []);

  const fetchMore = async () => {
    await dispatch(jobSearch.getPage({ page: jobSearchPageInfo.currentPage + 1 }));
  };

  const renderJobs = () => {
    if (jobSearchData && !jobSearchData.length && !jobSearchLoading) {
      return (
        <NoResultsMessage
          altTag="No projects"
          dusk="no-projects"
          image={jobsImage}
          message="Nothing to see here."
          subText="The selected filters have yielded 0 results."
        />
      );
    }

    return (
      <Box className="listJobsInfiniteScroll">
        <InfiniteScroll
          dataLength={jobSearchData.length}
          hasMore={jobSearchPageInfo.hasMore}
          height="77vh"
          loader={(<LoadingSpinner isInline />)}
          next={() => fetchMore()}
          ref={scrollRef}
          scrollableTarget="listJobsInfiniteScroll"
          scrollThreshold={0.9}
        >
          {jobSearchData.map(job => <JobRow job={job} key={job.id} />)}
        </InfiniteScroll>
      </Box>
    );
  };

  return (
    <Page fixedHeight backgroundColor="grey" dusk="projects-page">
      <JobsPageHeader />
      {jobSearchLoading ? <LoadingSpinner /> : renderJobs()}
    </Page>
  );
};

export default JobsPage;
